import type { UseMutationOptions } from '@tanstack/react-query'
import { useQueryClient, useMutation } from '@tanstack/react-query'

import type {
  PlaceBidBody,
  PlaceBidResponse as APIResponse,
} from '@b-stock/auction-api-client'
import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  BidStatus,
  getBidsClient,
  uniqueUserBidsQuery,
  type OrchestratedListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'

import {
  clearBetterOddsClickThrough,
  getBetterOddsClickThrough,
  trackBetterOddsBidPlaced,
} from '@/helpers/betterOdds/analytics'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import type { GaSessionIdPayload } from '@/helpers/telemetry/gaSessionIds'
import { getPersistedItemListContext } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import { useGaSessionIdPayload } from '@/helpers/telemetry/useGaSessionIdPayload'
import type {
  GTMBidSubmitEvent,
  GTMEcommerceItem,
} from '@/helpers/types/gtmTypes'
import listingDetailsQuery from '@/queries/listingDetailsQuery'

export type PlaceBidParams = {
  amount: number
  auctionId: string
  /** First Dibs buyer group ID. When set, the bid is treated as a First Dibs bid by the auction service. */
  buyerGroupId?: string
  bidLocation: 'pdp' | 'list' | 'dashboard'
  item?: GTMEcommerceItem
  isNewSeller?: boolean
  listingId: string
  userId?: string
}

type PlaceBidResponse = {
  data: APIResponse
  params: PlaceBidParams
}

const placeBid = async (
  params: PlaceBidParams,
  accessToken: string,
  gaSessionIdPayload: GaSessionIdPayload = {}
): Promise<PlaceBidResponse> => {
  const { auctionId, amount, buyerGroupId } = params
  const bidsClient = getBidsClient()

  // GLOB-4954: forward the GA client/session ids as top-level camelCase fields.
  // The generated PlaceBidBody type doesn't know these fields yet (client regen
  // follows the backend capture branch mg-GLOB-4949-capture-ga-ids), so we
  // extend the type locally. The payload comes from useGaSessionIdPayload(),
  // which yields `{}` unless the kill switch is on AND the GA cookies are
  // present — in which case the body is identical to before.
  // buyerGroupId: when present, the auction service treats this as a First Dibs bid.
  const placeBidBody: PlaceBidBody &
    GaSessionIdPayload & { buyerGroupId?: string } = {
    amount,
    ...gaSessionIdPayload,
    ...(buyerGroupId ? { buyerGroupId } : {}),
  }

  const response = await bidsClient.placeBid(
    { id: auctionId, placeBidBody },
    buildAxiosRequestConfig({ accessToken })
  )

  return { data: response.data, params }
}

const usePlaceBidMutation = (
  opts?: Omit<
    UseMutationOptions<PlaceBidResponse, unknown, PlaceBidParams>,
    'mutationFn'
  >
) => {
  const accessToken = useAccessToken()
  const queryClient = useQueryClient()
  const getGaSessionIdPayload = useGaSessionIdPayload()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: (params: PlaceBidParams) =>
      placeBid(params, accessToken || '', getGaSessionIdPayload()),
    onSuccess: ({ data, params }) => {
      // Read before setQueriesData below overwrites it — an existing bid
      // means this is a raise, not this buyer's first bid on the listing,
      // so no new unique bidder. Only matters for Better Odds attribution
      // just below; harmless read for every other bid.
      const previousListingData =
        queryClient.getQueryData<OrchestratedListingDetails>(
          listingDetailsQuery.getKey(accessToken, [params.listingId])
        )
      const isFirstBidOnListing = !previousListingData?.auction?.bid

      //NOTE: not the biggest fan of doing this here. Did this to try and keep the event firing in React.
      //Happy to talk about this approach. Maybe we trigger a JS event here and listen for it on the DOM?
      const listContext = getPersistedItemListContext(params.listingId)
      const bidSubmitEvent: GTMBidSubmitEvent = {
        event: 'bid_submit',
        platform: GTM_PLATFORM,
        item_id: params.listingId,
        bid_amount: params.amount,
        currency: 'USD',
        bid_location: params.bidLocation,
        is_first_bid: undefined, // TODO: populate from user auth state once backend work is done
        is_new_seller: params.isNewSeller,
        auctionId: params.auctionId,
        listingId: params.listingId,
      }

      if (params.item) {
        bidSubmitEvent.ecommerce = {
          currency: 'USD',
          items: [
            {
              ...params.item,
              item_list_id: listContext.itemListId,
              item_list_name: listContext.itemListName,
            },
          ],
        }
      }

      sendTypedGTMEvent(bidSubmitEvent)

      queryClient.setQueriesData(
        {
          queryKey: listingDetailsQuery.getKey(accessToken, [params.listingId]),
        },
        (listingData: unknown) => {
          if (!listingData) return listingData
          const castedListingData = listingData as OrchestratedListingDetails
          return {
            ...castedListingData,
            auction: {
              ...castedListingData.auction,
              ...data,
              userBidAmount: params.amount,
              bid: {
                bidAmount: params.amount,
              },
              bidStatus: data.winning ? BidStatus.WINNING : BidStatus.LOSING,
            },
          }
        }
      )

      void queryClient.invalidateQueries({
        queryKey: [uniqueUserBidsQuery.name],
      })

      const clickThrough = getBetterOddsClickThrough()
      if (clickThrough?.listingId === params.listingId) {
        trackBetterOddsBidPlaced(track, {
          targetListingId: params.listingId,
          wasFirstBidOnListing: isFirstBidOnListing,
        })
        clearBetterOddsClickThrough()
      }
    },
    ...opts,
  })
}

export default usePlaceBidMutation
